/**
 * AccountabilityUI — shared presentational primitives that port the APPROVED
 * accountability mockup (p7-accountability-integrated-v3) onto the real build.
 *
 * Pure look-and-feel, built ENTIRELY from the real var(--v2-*) design tokens
 * (Tailwind v2-* classes). No data lives here — every surface stays wired to its
 * own backend source. Shared so all five App-Pack tabs read as one system.
 */
import React from 'react';

/* ── Posture strip: compact baseline pills (mockup .ptile) ─────────────────── */

export function PostureStrip({ children }) {
  return <div className="flex flex-wrap gap-2.5 mb-4">{children}</div>;
}

/**
 * One posture tile. `tone`: 'default' | 'crit' (danger number) | 'good' (success).
 * `sub` renders a small trailing detail line under the label when provided.
 */
export function PTile({ value, label, tone = 'default', sub = null }) {
  const valueTone = tone === 'crit' ? 'text-v2-danger'
    : tone === 'good' ? 'text-v2-success'
    : 'text-v2-ink';
  return (
    <div className="inline-flex items-baseline gap-2.5 bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-[15px] py-2.5">
      <span className={`text-[19px] font-extrabold tracking-tight tabular-nums leading-none ${valueTone}`}>{value}</span>
      <span className="flex flex-col">
        <span className="text-[10px] font-bold uppercase tracking-wide text-v2-ink-muted">{label}</span>
        {sub ? <span className="text-[10px] text-v2-ink-muted/80 mt-0.5">{sub}</span> : null}
      </span>
    </div>
  );
}

/* ── Materiality tier badge: solid severity ramp (mockup .tier) ────────────── */

const TIER_BADGE = {
  TIER_1: 'bg-v2-danger text-white',
  TIER_2: 'bg-v2-warning text-white',
  TIER_3: 'bg-v2-cyan text-white',
  TIER_4: 'bg-v2-muted text-v2-ink-muted',
};

export function TierBadge({ tier, label }) {
  const cls = TIER_BADGE[tier] || 'bg-v2-muted text-v2-ink-muted';
  const text = label || (tier ? tier.replace('TIER_', 'Tier ') : 'unscored');
  return (
    <span className={`inline-flex items-center text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-v2-sm ${cls}`}>
      {text}
    </span>
  );
}

/* ── Owner avatar: initials chip (mockup .oav) ─────────────────────────────── */

function initials(name) {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '—';
}

export function OwnerAvatar({ name, tone = 'trust' }) {
  const bg = tone === 'danger' ? 'bg-v2-danger' : 'bg-v2-trust';
  return (
    <span className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full text-white text-[9px] font-bold shrink-0 ${bg}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/* ── Callout banner: left-accent notice (mockup .callout) ──────────────────── */

const CALLOUT_TONE = {
  warning: 'bg-v2-warning-soft border-l-v2-warning',
  danger: 'bg-v2-danger-soft border-l-v2-danger',
  trust: 'bg-v2-muted border-l-v2-trust',
};

export function Callout({ tone = 'warning', children, className = '' }) {
  const cls = CALLOUT_TONE[tone] || CALLOUT_TONE.warning;
  return (
    <div className={`border border-v2-border-subtle border-l-[3px] rounded-v2-md px-3.5 py-2.5 text-[12.5px] text-v2-ink-secondary ${cls} ${className}`}>
      {children}
    </div>
  );
}

/* ── Section header (mockup .sechead / .secsub) for card bodies ─────────────── */

export function SectionHead({ title, sub }) {
  return (
    <div className="mb-3">
      <div className="text-[14px] font-bold text-v2-ink">{title}</div>
      {sub ? <div className="text-[11.5px] text-v2-ink-muted mt-0.5">{sub}</div> : null}
    </div>
  );
}

/* ── State-machine flow (mockup .sm/.flow/.st) ─────────────────────────────── */

const STATE_NODE = {
  default: 'border-v2-border text-v2-ink bg-v2-surface',
  gate: 'border-v2-action text-v2-action bg-v2-action-soft',
  ok: 'border-v2-success text-v2-success bg-v2-success-soft',
  bad: 'border-v2-danger text-v2-danger bg-v2-danger-soft',
  dash: 'border-dashed border-v2-border-strong text-v2-ink-muted bg-v2-surface',
};

export function StateNode({ children, variant = 'default', count = null }) {
  const cls = STATE_NODE[variant] || STATE_NODE.default;
  return (
    <span className={`inline-flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-[11px] font-bold tracking-wide whitespace-nowrap ${cls}`}>
      {children}
      {count != null ? <span className="tabular-nums opacity-70">· {count}</span> : null}
    </span>
  );
}

export function FlowArrow({ label, glyph = '→' }) {
  return (
    <span className="inline-flex items-center gap-1 text-v2-ink-muted text-[12px] px-0.5">
      {label ? <span className="text-[10.5px]">{label}</span> : null}
      <span aria-hidden="true">{glyph}</span>
    </span>
  );
}

export function FlowRow({ children, className = '' }) {
  return <div className={`flex items-center flex-wrap gap-2 mb-2 ${className}`}>{children}</div>;
}

export function FlowLabel({ children }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-wider text-v2-ink-muted mt-4 mb-2 first:mt-0">
      {children}
    </div>
  );
}

/** A titled panel that frames one sub-flow of the state machine (mockup .sm sub-section). */
export function FlowPanel({ title, children }) {
  return (
    <div className="border border-v2-border-subtle rounded-v2-md p-3 bg-v2-surface">
      <div className="text-[10px] font-bold uppercase tracking-wider text-v2-ink-muted mb-2.5">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
